// src/services/events.ts
import api, { webApi, getCsrfToken } from "../api";
import { Event, EventCreateRequest, EventUpdateRequest } from "../types";

/**
 * Fetch all upcoming events with pagination
 */
export function getEvents() {
  return api.get("/events");
}

/**
 * Fetch a single event by ID
 */
export function getEvent(id: string) {
  return api.get<Event>(`/events/${id}`);
}

/**
 * Create a new event (admin only)
 */
export async function createEvent(payload: EventCreateRequest) {
  await getCsrfToken();
  return api.post<Event>("/admin/events", payload);
}

/**
 * Update an existing event (admin only)
 */
export async function updateEvent(
  id: string | number,
  payload: EventUpdateRequest
) {
  await getCsrfToken();
  try {
    return await api.put<Event>(`/admin/events/${id}`, payload);
  } catch (err: any) {
    // Log error for debugging but re-throw instead of silent fallback
    console.error("Event update failed:", err);
    throw err;
  }
}

/**
 * Delete an event (admin only)
 */
export async function deleteEvent(id: string | number) {
  await getCsrfToken();
  return api.delete(`/admin/events/${id}`);
}

/**
 * Toggle attendance status for a user at an event
 */
export async function toggleAttendance(
  eventId: string | number,
  rsvpId: string | number
) {
  await getCsrfToken();
  return api.post(`/admin/events/${eventId}/attendance/${rsvpId}`);
}

/**
 * Submit or update an RSVP for an event
 */
export async function rsvpEvent(
  eventId: string | number,
  status: "going" | "maybe" | "not_going" = "going"
) {
  await getCsrfToken();
  return api.post(`/events/${eventId}/rsvp`, { status });
}

/**
 * Cancel a user's RSVP for an event
 */
export async function cancelRsvpEvent(eventId: string | number) {
  await getCsrfToken();
  return api.delete(`/events/${eventId}/rsvp`);
}
